import { CopyPlus, Pause, Play } from 'lucide-react'
import type { StrategyView } from '@/lib/api-types'
import { tokenLogo } from '@/lib/default-tokens'
import { StatusChip, type StatusTone } from './status-chip'
import { Ticker } from './ticker'
import { useToast } from './toast'
import { useStrategyActions } from '@/lib/strategy-actions'

export const STATUS_LABEL: Record<StrategyView['status'], string> = {
  scheduled: 'Scheduled',
  active: 'Active',
  paused: 'Paused',
  completed: 'Complete',
  cancelled: 'Stopped',
  failed: 'Failed',
}

/** a strategy's state on the shared chip (see status-chip.tsx): running green, scheduled amber, paused grey, completed faded, stopped or failed red */
const STATUS_TONE: Record<StrategyView['status'], StatusTone> = {
  scheduled: 'waiting',
  active: 'live',
  paused: 'resting',
  completed: 'done',
  cancelled: 'bad',
  failed: 'bad',
}

export function StatusBadge({ status }: { status: StrategyView['status'] }) {
  return <StatusChip tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</StatusChip>
}

export function strategyKind(s: StrategyView) {
  return `${s.kind === 'limit' ? 'Limit' : 'DCA'} ${s.side === 'sell' ? 'Sell' : 'Buy'}`
}

/** plain-text form: ticker, then the order type and side */
export function strategyTitle(s: StrategyView) {
  return `${(s.token.symbol ?? 'token').toUpperCase()} ${strategyKind(s)}`
}

/** the form for toasts and the activity bar: the logo leads the ticker, then the order type and side (John, Oct 31 2026) */
export function StrategyToastTitle({ strategy: s }: { strategy: StrategyView }) {
  return (
    <>
      <Ticker symbol={(s.token.symbol ?? 'token').toUpperCase()} imageUrl={tokenLogo(s.token.address, s.token.imageUrl ?? undefined)} /> {strategyKind(s)}
    </>
  )
}

/** ticker, a thin divider, then the order type and side (John, Oct 30 2026); the logo sits next to it */
export function StrategyTitle({ strategy: s, className = '' }: { strategy: StrategyView; className?: string }) {
  return (
    <span className={`flex min-w-0 items-center gap-2.5 ${className}`}>
      <span className="whitespace-nowrap font-semibold">{(s.token.symbol ?? 'token').toUpperCase()}</span>
      <span className="h-4 w-px shrink-0 bg-line" aria-hidden="true" />
      <span className="whitespace-nowrap font-medium">{strategyKind(s)}</span>
    </span>
  )
}

/** a paused or failed strategy goes on from its next order, and a stopped one too while it has orders left (John, Nov 4 2026: "I should be able to resume a stopped one"); the detail dialog offers it, the row keeps Duplicate */
export function canResumeStrategy(s: StrategyView): boolean {
  return s.status === 'paused' || s.status === 'failed' || (s.status === 'cancelled' && s.sentOrders < s.dca.orders)
}

/** pause or resume from the strategy's row (John, Oct 30 2026); the row stays clickable around it */
export function PauseResumeButton({ strategy: s }: { strategy: StrategyView }) {
  const toast = useToast()
  // the row answers the press at once and the server confirms it after (lib/strategy-actions)
  const { pause, resume } = useStrategyActions((action, message) => toast('error', action === 'pause' ? 'Could not pause' : 'Could not resume', message))
  const canPause = s.status === 'active' || s.status === 'scheduled'
  const canResume = canResumeStrategy(s)
  if (!canPause && !canResume) return null
  const busy = pause.isPending || resume.isPending
  return (
    <button
      type="button"
      className="btn btn-ghost !p-1.5 disabled:opacity-50"
      aria-label={canPause ? 'Pause strategy' : 'Resume strategy'}
      title={canPause ? 'Pause' : 'Resume'}
      disabled={busy}
      onClick={(event) => {
        event.stopPropagation()
        if (canPause) pause.mutate({ id: s.id })
        else resume.mutate({ id: s.id })
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {canPause ? <Pause className="size-4" /> : <Play className="size-4" />}
    </button>
  )
}

/**
 * A finished row's action (John, Nov 3 2026): where a live row pauses or resumes, a completed or stopped one duplicates,
 * opening the wizard filled in with the strategy so it runs again as it was, or changed a little. Enter and Space stay
 * with the button (the row would open the detail); Escape must still reach a dialog's window listener.
 */
export function DuplicateButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="btn btn-ghost !p-1.5"
      aria-label="Duplicate strategy"
      title="Duplicate"
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
      }}
    >
      <CopyPlus className="size-4" />
    </button>
  )
}
